/**
 * API client for communicating with the backend.
 * Uses axios for HTTP requests with automatic JWT token injection.
 */

import axios from 'axios';
import type {
    Envelope,
    EnvelopeDetail,
    EnvelopeListResponse,
    EnvelopeCreate,
    EnvelopeUpdate,
    LoginRequest,
    Token,
    Media,
} from '@/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
// Log which API URL is being used at runtime for diagnostics
console.log('[API] Base URL:', API_URL);

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add JWT token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('🔑 Token attached to request:', config.url, token.substring(0, 20) + '...');
    } else {
        console.warn('⚠️ No token found for request:', config.url);
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor to handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Only clear token and redirect if we're not on the login page
            // and if this isn't a login request failure
            const isLoginRequest = error.config?.url?.includes('/api/auth/login') || error.config?.url?.includes('/api/auth/google');
            const isLoginPage = window.location.pathname.includes('/admin/login');
            
            if (!isLoginRequest && !isLoginPage) {
                // Unauthorized - clear token and redirect to login
                localStorage.removeItem('access_token');
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(error);
    }
);

// ============= Authentication =============

export const login = async (credentials: LoginRequest): Promise<Token> => {
    const response = await api.post<Token>('/api/auth/login', credentials);
    return response.data;
};

// ============= Public Envelope Endpoints =============

export const getEnvelopes = async (page = 1, pageSize = 12): Promise<EnvelopeListResponse> => {
    const response = await api.get<EnvelopeListResponse>('/api/envelopes', {
        params: { page, page_size: pageSize },
    });
    return response.data;
};

export const getEnvelopeBySlug = async (slug: string): Promise<EnvelopeDetail> => {
    const response = await api.get<EnvelopeDetail>(`/api/envelopes/${slug}`);
    return response.data;
};

// ============= Admin Envelope Endpoints =============

export const getAllEnvelopes = async (page = 1, pageSize = 12): Promise<EnvelopeListResponse> => {
    const response = await api.get<EnvelopeListResponse>('/api/admin/envelopes', {
        params: { page, page_size: pageSize },
    });
    return response.data;
};

export const createEnvelope = async (data: EnvelopeCreate): Promise<Envelope> => {
    const response = await api.post<Envelope>('/api/admin/envelopes', data);
    return response.data;
};

export const updateEnvelope = async (id: number, data: EnvelopeUpdate): Promise<Envelope> => {
    const response = await api.put<Envelope>(`/api/admin/envelopes/${id}`, data);
    return response.data;
};

export const deleteEnvelope = async (id: number): Promise<void> => {
    await api.delete(`/api/admin/envelopes/${id}`);
};

// ============= Media Endpoints =============

export const uploadMedia = async (file: File): Promise<Media> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<Media>('/api/admin/media', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const getMediaList = async (): Promise<Media[]> => {
    const response = await api.get<Media[]>('/api/admin/media');
    return response.data;
};

export default api;
